import React, { useState, useEffect, useMemo } from 'react';

const IP = () => {
  const [activeSection, setActiveSection] = useState('executive-summary');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Table of contents data
  const tableOfContents = useMemo(() => [
    { id: 'executive-summary', title: '1. Executive Summary', level: 1 },
    { id: 'strategic-objectives', title: 'Strategic IP Objectives', level: 2 },
    { id: 'portfolio-overview', title: 'IP Portfolio Overview', level: 2 },
    { id: 'investment-summary', title: 'Total IP Investment: $940K Over 18 Months', level: 2 },
    { id: 'patent-portfolio', title: '2. Patent Portfolio Strategy', level: 1 },
    { id: 'core-patents', title: 'Core Business Model Patents (Priority Tier 1)', level: 2 },
    { id: 'fractional-platform', title: 'Fractional Real Estate Platform Patents', level: 3 },
    { id: 'three-sided-marketplace', title: 'Three-Sided Marketplace Patents', level: 3 },
    { id: 'renter-equity', title: 'Renter Equity Building Patents', level: 3 },
    { id: 'technology-patents', title: 'Technology and Process Patents (Priority Tier 2)', level: 2 },
    { id: 'marketplace-tech', title: 'Marketplace-Specific Technology Patents', level: 3 },
    { id: 'mobile-voting', title: 'Mobile Voting and Governance Patents', level: 3 },
    { id: 'investment-transaction', title: 'Investment and Transaction Patents', level: 3 },
    { id: 'defensive-patents', title: 'Defensive and Strategic Patents (Priority Tier 3)', level: 2 },
    { id: 'risk-management', title: 'Risk Management and Security Patents', level: 3 },
    { id: 'integration-future', title: 'Integration and Future Technology Patents', level: 3 },
    { id: 'trade-secrets', title: '3. Trade Secrets & Proprietary Methods', level: 1 },
    { id: 'operational-secrets', title: 'Operational Trade Secrets', level: 2 },
    { id: 'financial-methodologies', title: 'Financial and Pricing Methodologies', level: 2 },
    { id: 'technology-secrets', title: 'Technology and Platform Trade Secrets', level: 2 },
    { id: 'trademark-brand', title: '4. Trademark & Brand Protection', level: 1 },
    { id: 'core-trademarks', title: 'Core Brand Trademarks', level: 2 },
    { id: 'value-slogans', title: 'Value Proposition Slogans', level: 2 },
    { id: 'international-strategy', title: '5. International IP Strategy', level: 1 },
    { id: 'global-filing', title: 'Global Patent Filing Strategy', level: 2 },
    { id: 'regional-strategies', title: 'Regional IP Strategies', level: 2 },
    { id: 'valuation-licensing', title: '6. IP Portfolio Valuation & Licensing', level: 1 },
    { id: 'portfolio-valuation', title: 'Patent Portfolio Valuation', level: 2 },
    { id: 'licensing-revenue', title: 'Licensing Revenue Strategy', level: 2 },
    { id: 'implementation', title: '7. Implementation Timeline', level: 1 },
    { id: 'phase-1', title: 'Phase 1: Foundation and Core Patents (Months 1-6)', level: 2 },
    { id: 'phase-2', title: 'Phase 2: Technology and Expansion (Months 6-12)', level: 2 },
    { id: 'phase-3', title: 'Phase 3: Defense and Optimization (Months 12-18)', level: 2 },
    { id: 'roi-projections', title: '8. Return on Investment Projections', level: 1 },
    { id: 'value-creation', title: 'IP Value Creation Timeline', level: 2 },
    { id: 'cost-benefit', title: 'Cost-Benefit Analysis', level: 2 }
  ], []);

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      for (let i = tableOfContents.length - 1; i >= 0; i--) {
        const element = document.getElementById(tableOfContents[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: "'Inter', sans-serif"
    },
    sidebar: {
      width: sidebarOpen ? '320px' : '0px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
      position: 'fixed',
      height: '100vh',
      overflowY: 'auto',
      transition: 'width 0.3s ease',
      zIndex: 1000,
      boxShadow: sidebarOpen ? '2px 0 10px rgba(0,0,0,0.1)' : 'none'
    },
    sidebarToggle: {
      position: 'fixed',
      top: '20px',
      left: sidebarOpen ? '330px' : '10px',
      zIndex: 1001,
      backgroundColor: '#667eea',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      cursor: 'pointer',
      fontSize: '18px',
      transition: 'left 0.3s ease',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
    },
    sidebarHeader: {
      padding: '20px',
      borderBottom: '1px solid rgba(0,0,0,0.1)',
      backgroundColor: 'rgba(102, 126, 234, 0.1)'
    },
    sidebarTitle: {
      margin: '0 0 10px 0',
      fontSize: '20px',
      fontWeight: '700',
      color: '#333'
    },
    pdfLink: {
      display: 'inline-block',
      padding: '8px 16px',
      backgroundColor: '#667eea',
      color: 'white',
      textDecoration: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'background-color 0.2s ease'
    },
    tocList: {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    tocItem: {
      margin: '0'
    },
    tocButton: {
      width: '100%',
      padding: '8px 20px',
      border: 'none',
      backgroundColor: 'transparent',
      textAlign: 'left',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      color: '#555'
    },
    tocButtonLevel1: {
      fontWeight: '700',
      fontSize: '15px',
      color: '#333',
      borderLeft: '4px solid transparent'
    },
    tocButtonLevel2: {
      paddingLeft: '30px',
      fontWeight: '500'
    },
    tocButtonLevel3: {
      paddingLeft: '45px',
      fontSize: '13px',
      color: '#666'
    },
    tocButtonActive: {
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      borderLeftColor: '#667eea',
      color: '#667eea',
      fontWeight: '600'
    },
    content: {
      padding: '40px',
      minHeight: '100vh',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      maxWidth: '900px',
      margin: '0 auto',
      marginLeft: sidebarOpen ? '320px' : '0px'
    },
    section: {
      marginBottom: '40px'
    },
    h1: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#333',
      marginBottom: '24px',
      borderBottom: '3px solid #667eea',
      paddingBottom: '12px'
    },
    h2: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#444',
      marginBottom: '20px',
      marginTop: '32px'
    },
    h3: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#555',
      marginBottom: '16px',
      marginTop: '24px'
    },
    h4: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#666',
      marginBottom: '12px',
      marginTop: '20px'
    },
    paragraph: {
      fontSize: '16px',
      lineHeight: '1.6',
      color: '#555',
      marginBottom: '16px'
    },
    bulletList: {
      paddingLeft: '20px',
      marginBottom: '20px'
    },
    bulletItem: {
      fontSize: '16px',
      lineHeight: '1.6',
      color: '#555',
      marginBottom: '8px'
    },
    highlightBox: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '24px',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
    },
    noteBox: {
      backgroundColor: '#f8f9ff',
      border: '1px solid #e1e5f2',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    successBox: {
      backgroundColor: '#f0f9f4',
      border: '1px solid #a7f3d0',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    warningBox: {
      backgroundColor: '#fffbeb',
      border: '1px solid #fed7aa',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    riskBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '24px',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    tableHeader: {
      backgroundColor: '#667eea',
      color: 'white'
    },
    tableHeaderCell: {
      padding: '16px',
      textAlign: 'left',
      fontWeight: '600',
      fontSize: '14px'
    },
    tableCell: {
      padding: '14px 16px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px',
      color: '#374151'
    },
    tableRowEven: {
      backgroundColor: '#f9fafb'
    },
    metricGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    },
    metricCard: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    metricNumber: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#667eea',
      marginBottom: '8px'
    },
    metricLabel: {
      fontSize: '14px',
      color: '#666',
      fontWeight: '500'
    }
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <button 
          style={styles.sidebarToggle}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? '‹' : '›'}
        </button>
        
        {sidebarOpen && (
          <>
            <div style={styles.sidebarHeader}>
              <h2 style={styles.sidebarTitle}>IP Strategy</h2>
              <a 
                href="/ip-strategy.pdf" 
                style={styles.pdfLink} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                📄 Download PDF
              </a>
            </div>
            
            <ul style={styles.tocList}>
              {tableOfContents.map((item) => (
                <li key={item.id} style={styles.tocItem}>
                  <button
                    style={{
                      ...styles.tocButton,
                      ...(item.level === 1 ? styles.tocButtonLevel1 : 
                          item.level === 2 ? styles.tocButtonLevel2 : styles.tocButtonLevel3),
                      ...(activeSection === item.id ? styles.tocButtonActive : {})
                    }}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        <div style={styles.highlightBox}>
          <h1 style={{...styles.h1, color: 'white', border: 'none', fontSize: '42px'}}>
            GusHome Intellectual Property Strategy
          </h1>
          <p style={{fontSize: '20px', margin: '0', opacity: '0.9'}}>
            Comprehensive Patent Portfolio & Trade Secret Protection
          </p>
          <p style={{fontSize: '16px', margin: '10px 0 0 0', opacity: '0.8'}}>
            Confidential & Proprietary | Strategic IP Protection for Market Leadership | June 2025
          </p>
        </div>

        {/* Executive Summary */}
        <section id="executive-summary" style={styles.section}>
          <h1 style={styles.h1}>1. Executive Summary</h1>
          
          <p style={styles.paragraph}>
            GusHome's intellectual property strategy creates multiple layers of protection around our revolutionary real estate marketplace model. Our comprehensive IP portfolio establishes defensible competitive moats while enabling licensing opportunities and strategic partnerships.
          </p>

          <div id="strategic-objectives" style={styles.section}>
            <h2 style={styles.h2}>Strategic IP Objectives</h2>
            
            <div style={styles.metricGrid}>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>39</div>
                <div style={styles.metricLabel}>Total Patent Applications</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>12</div>
                <div style={styles.metricLabel}>Trade Secret Categories</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>14</div>
                <div style={styles.metricLabel}>Trademark Registrations</div>
              </div>
            </div>

            <h3 style={styles.h3}>Market Defense:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Patent Thicket:</strong> Multiple overlapping patents prevent competitor copying</li>
              <li style={styles.bulletItem}><strong>Trade Secret Protection:</strong> Operational advantages through proprietary methods</li>
              <li style={styles.bulletItem}><strong>Brand Recognition:</strong> Trademark protection for market positioning</li>
            </ul>

            <h3 style={styles.h3}>Revenue Generation:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Licensing Opportunities:</strong> Patent licensing to real estate and fintech companies</li>
              <li style={styles.bulletItem}><strong>Strategic Partnerships:</strong> IP-enabled partnerships with major platforms</li>
              <li style={styles.bulletItem}><strong>Exit Value Enhancement:</strong> Strong IP portfolio increases acquisition value</li>
            </ul>
          </div>

          <div id="portfolio-overview" style={styles.section}>
            <h2 style={styles.h2}>IP Portfolio Overview</h2>
            
            <h3 style={styles.h3}>Patent Applications: 39 Total</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Core Business Model Patents (12):</strong> Fundamental marketplace mechanics</li>
              <li style={styles.bulletItem}><strong>Technology Patents (15):</strong> Platform functionality and algorithms</li>
              <li style={styles.bulletItem}><strong>Defensive Patents (12):</strong> Competitive protection and future innovation</li>
            </ul>

            <h3 style={styles.h3}>Trade Secrets: 12 Categories</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Operational Methodologies:</strong> Landlord acquisition, user retention</li>
              <li style={styles.bulletItem}><strong>Algorithms:</strong> Pricing, matching, risk assessment</li>
              <li style={styles.bulletItem}><strong>Legal Frameworks:</strong> State-specific templates and compliance playbooks</li>
            </ul>

            <h3 style={styles.h3}>Trademark Protection: 14 Marks</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Brand Names:</strong> GusHome and related product names</li>
              <li style={styles.bulletItem}><strong>Slogans:</strong> Core value propositions and marketing messages</li>
              <li style={styles.bulletItem}><strong>Digital Assets:</strong> Domain names and social media protection</li>
            </ul>
          </div>

          <div id="investment-summary" style={styles.section}>
            <h2 style={styles.h2}>Total IP Investment: $940K Over 18 Months</h2>
            
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Investment Category</th>
                  <th style={styles.tableHeaderCell}>Amount</th>
                  <th style={styles.tableHeaderCell}>Percentage</th>
                  <th style={styles.tableHeaderCell}>Timeline</th>
                </tr>
              </thead>
              <tbody>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Patent Portfolio Development</td>
                  <td style={styles.tableCell}>$700K</td>
                  <td style={styles.tableCell}>74%</td>
                  <td style={styles.tableCell}>18 months</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>Trade Secret & Legal Framework</td>
                  <td style={styles.tableCell}>$150K</td>
                  <td style={styles.tableCell}>16%</td>
                  <td style={styles.tableCell}>18 months</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Trademark & Brand Protection</td>
                  <td style={styles.tableCell}>$50K</td>
                  <td style={styles.tableCell}>5%</td>
                  <td style={styles.tableCell}>12 months</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>Enforcement & Monitoring</td>
                  <td style={styles.tableCell}>$40K</td>
                  <td style={styles.tableCell}>4%</td>
                  <td style={styles.tableCell}>Ongoing</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Patent Portfolio Strategy */}
        <section id="patent-portfolio" style={styles.section}>
          <h1 style={styles.h1}>2. Patent Portfolio Strategy</h1>

          <div id="core-patents" style={styles.section}>
            <h2 style={styles.h2}>Core Business Model Patents (Priority Tier 1)</h2>
            <p style={styles.paragraph}>
              Our foundational patents protect the core innovations that make GusHome's marketplace model unique and defensible.
            </p>

            <div id="fractional-platform" style={styles.section}>
              <h3 style={styles.h3}>Fractional Real Estate Platform Patents</h3>
              
              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 1: "Method and System for Fractional Real Estate Investment Through Tenancy-in-Common Structures"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Novel system for creating liquid, tradeable fractional interests in real property through automated TIC formation and management.</p>
                <p style={styles.paragraph}><strong>Key Claims:</strong></p>
                <ul style={styles.bulletList}>
                  <li style={styles.bulletItem}>Automated conversion of whole property ownership into fractional TIC interests</li>
                  <li style={styles.bulletItem}>Digital platform for TIC interest trading with SEC compliance</li>
                  <li style={styles.bulletItem}>Integration of property management with fractional ownership governance</li>
                  <li style={styles.bulletItem}>Real-time valuation and pricing mechanisms for TIC interests</li>
                </ul>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 2: "Mobile Platform for Direct Property Ownership via Fractional Interests"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Mobile application system enabling direct property ownership through fractional interests with stock-market-like user experience.</p>
                <p style={styles.paragraph}><strong>Commercial Value:</strong> Protects mobile-first marketplace approach and user experience innovations</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 3: "Automated TIC Formation and Management System"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Automated system for legal entity formation, documentation generation, and ongoing management of TIC structures.</p>
                <p style={styles.paragraph}><strong>Commercial Value:</strong> Protects cost advantages in legal processing and enables B2B licensing opportunities</p>
              </div>
            </div>

            <div id="three-sided-marketplace" style={styles.section}>
              <h3 style={styles.h3}>Three-Sided Marketplace Patents</h3>
              
              <div style={styles.successBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 5: "Three-Sided Real Estate Marketplace with Landlord, Tenant, and Investor Participants"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Marketplace platform connecting landlords (supply), tenants (demand creators), and investors (capital providers) in unified ecosystem.</p>
                <p style={styles.paragraph}><strong>Strategic Value:</strong> Core patent for marketplace licensing and competitive defense against direct copying of three-sided approach</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 8: "Zero-Fee Landlord Partnership Model for Real Estate Platforms"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Business model innovation enabling free platform access for property owners while generating revenue from other participants.</p>
                <p style={styles.paragraph}><strong>Key Claims:</strong> Revenue model generating income from investors/tenants, not landlords</p>
              </div>
            </div>

            <div id="renter-equity" style={styles.section}>
              <h3 style={styles.h3}>Renter Equity Building Patents</h3>
              
              <div style={styles.successBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 9: "Automatic Rent-to-Equity Conversion System"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Automated system converting portion of rental payments into property ownership stakes.</p>
                <p style={styles.paragraph}><strong>Commercial Value:</strong> Protects core renter value proposition and enables licensing to property management companies</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 12: "Integrated Housing and Investment Platform for Renters"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Unified platform combining housing search, rental management, and investment functionality for renters.</p>
                <p style={styles.paragraph}><strong>Social Impact:</strong> Protects wealth-building innovation for renters</p>
              </div>
            </div>
          </div>

          <div id="technology-patents" style={styles.section}>
            <h2 style={styles.h2}>Technology and Process Patents (Priority Tier 2)</h2>

            <div id="marketplace-tech" style={styles.section}>
              <h3 style={styles.h3}>Marketplace-Specific Technology Patents</h3>
              
              <div style={styles.warningBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 13: "Dynamic Pricing Algorithm for Real Estate Marketplace Liquidity"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Algorithm dynamically adjusting property share prices based on supply, demand, and market conditions to optimize liquidity.</p>
                <p style={styles.paragraph}><strong>Commercial Value:</strong> Protects competitive pricing algorithms and enables superior price discovery</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 15: "Network Effects Optimization for Three-Sided Property Marketplaces"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> System optimizing network effects and participant engagement across landlord-tenant-investor ecosystem.</p>
              </div>
            </div>

            <div id="mobile-voting" style={styles.section}>
              <h3 style={styles.h3}>Mobile Voting and Governance Patents</h3>
              
              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 17: "Mobile Voting System for Fractional Property Owners"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Secure mobile voting platform enabling fractional property owners to participate in property management decisions.</p>
                <p style={styles.paragraph}><strong>Key Features:</strong> Blockchain verification, weighted voting, audit trails</p>
              </div>
            </div>

            <div id="investment-transaction" style={styles.section}>
              <h3 style={styles.h3}>Investment and Transaction Patents</h3>
              
              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 21: "Dollar-Cost Averaging for Real Estate Investment Platforms"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> System enabling systematic, regular investment in real estate through dollar-cost averaging strategies.</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 24: "Secondary Market for Fractional Real Estate Ownership Interests"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Secondary trading platform specifically designed for fractional real estate ownership interests.</p>
              </div>
            </div>
          </div>

          <div id="defensive-patents" style={styles.section}>
            <h2 style={styles.h2}>Defensive and Strategic Patents (Priority Tier 3)</h2>

            <div id="risk-management" style={styles.section}>
              <h3 style={styles.h3}>Risk Management and Security Patents</h3>
              
              <div style={styles.riskBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 28: "Fraud Detection System for Fractional Real Estate Transactions"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Machine learning system detecting fraudulent activity in fractional real estate transactions and marketplace activity.</p>
                <p style={styles.paragraph}><strong>Key Features:</strong> ML fraud detection, pattern recognition, real-time transaction blocking</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 30: "Anti-Money Laundering Compliance for Real Estate Marketplaces"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Comprehensive AML system designed for real estate marketplace transactions and fractional ownership.</p>
              </div>
            </div>

            <div id="integration-future" style={styles.section}>
              <h3 style={styles.h3}>Integration and Future Technology Patents</h3>
              
              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 35: "Blockchain Integration for Property Ownership Verification"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> Blockchain-based system for immutable property ownership verification and transfer recording.</p>
                <p style={styles.paragraph}><strong>Future Value:</strong> Positions GusHome for blockchain real estate evolution</p>
              </div>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Patent 36: "Machine Learning for Property Investment Recommendation"</h4>
                <p style={styles.paragraph}><strong>Innovation:</strong> ML system providing personalized property investment recommendations based on user profile and market analysis.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Trade Secrets */}
        <section id="trade-secrets" style={styles.section}>
          <h1 style={styles.h1}>3. Trade Secrets & Proprietary Methods</h1>

          <div id="operational-secrets" style={styles.section}>
            <h2 style={styles.h2}>Operational Trade Secrets</h2>
            
            <div style={styles.successBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Landlord Acquisition Methodology: "The Partnership Pyramid"</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Qualification Framework:</strong> Proprietary scoring system for landlord quality</li>
                <li style={styles.bulletItem}><strong>Approach Strategy:</strong> Multi-touch engagement process with proven conversion rates</li>
                <li style={styles.bulletItem}><strong>Partnership Terms:</strong> Optimized legal and financial structures for landlord retention</li>
                <li style={styles.bulletItem}><strong>Scaling Process:</strong> Replicable framework for geographic expansion</li>
              </ul>
              <p style={styles.paragraph}><strong>Competitive Advantage:</strong> 15-20% superior retention vs. generic platforms</p>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>User Retention and Engagement Algorithms: "Engagement Optimization Engine"</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Behavioral Analytics:</strong> Proprietary user behavior prediction models</li>
                <li style={styles.bulletItem}><strong>Intervention Triggers:</strong> Automated systems for user re-engagement</li>
                <li style={styles.bulletItem}><strong>Personalization Algorithms:</strong> Individual user experience optimization</li>
                <li style={styles.bulletItem}><strong>Churn Prevention:</strong> Predictive models for user retention</li>
              </ul>
            </div>
          </div>

          <div id="financial-methodologies" style={styles.section}>
            <h2 style={styles.h2}>Financial and Pricing Methodologies</h2>
            
            <div style={styles.warningBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Property Underwriting and Selection Criteria: "Quality Score Matrix"</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Financial Analysis:</strong> Proprietary cash flow and return analysis models</li>
                <li style={styles.bulletItem}><strong>Market Analysis:</strong> Location and neighborhood scoring algorithms</li>
                <li style={styles.bulletItem}><strong>Risk Assessment:</strong> Property-specific risk evaluation framework</li>
                <li style={styles.bulletItem}><strong>Growth Potential:</strong> Appreciation and rental growth prediction models</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Pricing Optimization Models: "Dynamic Pricing Engine"</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Market Data Integration:</strong> Proprietary weighting of multiple data sources</li>
                <li style={styles.bulletItem}><strong>Liquidity Optimization:</strong> Pricing strategies to maximize trading volume</li>
                <li style={styles.bulletItem}><strong>Risk-Adjusted Pricing:</strong> Real-time risk assessment and pricing adjustment</li>
                <li style={styles.bulletItem}><strong>Performance Tracking:</strong> Continuous optimization based on transaction outcomes</li>
              </ul>
            </div>
          </div>

          <div id="technology-secrets" style={styles.section}>
            <h2 style={styles.h2}>Technology and Platform Trade Secrets</h2>
            
            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>User Experience Optimization: "Conversion Optimization Framework"</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Onboarding Process:</strong> Optimized user journey for maximum conversion</li>
                <li style={styles.bulletItem}><strong>Interface Design:</strong> Proprietary UI/UX patterns for real estate investment</li>
                <li style={styles.bulletItem}><strong>Educational Content:</strong> Curated content strategy for user education</li>
                <li style={styles.bulletItem}><strong>Behavioral Nudges:</strong> Psychological optimization for user engagement</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Market Intelligence Platform</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Data Collection:</strong> Proprietary data sources and collection methods</li>
                <li style={styles.bulletItem}><strong>Analysis Algorithms:</strong> Custom analytics for real estate marketplace optimization</li>
                <li style={styles.bulletItem}><strong>Predictive Models:</strong> Forecasting models for user behavior and market trends</li>
                <li style={styles.bulletItem}><strong>Competitive Intelligence:</strong> Market analysis and competitive positioning insights</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Trademark & Brand Protection */}
        <section id="trademark-brand" style={styles.section}>
          <h1 style={styles.h1}>4. Trademark & Brand Protection</h1>

          <div id="core-trademarks" style={styles.section}>
            <h2 style={styles.h2}>Core Brand Trademarks</h2>
            
            <div style={styles.successBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Primary Company Marks</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>"GusHome" (Word Mark and Logo):</strong> Company name, platform branding, all business activities</li>
                <li style={styles.bulletItem}><strong>"GusHome Platform" (Service Mark):</strong> Platform services and technology offerings</li>
                <li style={styles.bulletItem}><strong>Classifications:</strong> IC 009 (Software), IC 035 (Business Services), IC 036 (Financial Services)</li>
              </ul>
            </div>
          </div>

          <div id="value-slogans" style={styles.section}>
            <h2 style={styles.h2}>Value Proposition Slogans</h2>
            
            <div style={styles.metricGrid}>
              <div style={styles.metricCard}>
                <h4 style={{...styles.h4, marginTop: '0'}}>"Turn your rent into ownership"</h4>
                <p style={{fontSize: '14px', color: '#666'}}>Core renter value proposition and marketing</p>
              </div>
              <div style={styles.metricCard}>
                <h4 style={{...styles.h4, marginTop: '0'}}>"The stock market for property"</h4>
                <p style={{fontSize: '14px', color: '#666'}}>Market positioning and investor messaging</p>
              </div>
              <div style={styles.metricCard}>
                <h4 style={{...styles.h4, marginTop: '0'}}>"Live here. Own here."</h4>
                <p style={{fontSize: '14px', color: '#666'}}>Integrated housing and investment messaging</p>
              </div>
            </div>

            <h3 style={styles.h3}>Segment-Specific Messaging:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Landlord-Focused:</strong> "Your property becomes a stock" - Equity liquidity messaging</li>
              <li style={styles.bulletItem}><strong>Investor-Focused:</strong> "Own properties like Tesla stock" - Accessibility and familiarity messaging</li>
              <li style={styles.bulletItem}><strong>Market Innovation:</strong> "Real estate just got unbundled" - Disruption and innovation messaging</li>
            </ul>
          </div>
        </section>

        {/* International IP Strategy */}
        <section id="international-strategy" style={styles.section}>
          <h1 style={styles.h1}>5. International IP Strategy</h1>

          <div id="global-filing" style={styles.section}>
            <h2 style={styles.h2}>Global Patent Filing Strategy</h2>
            
            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Patent Cooperation Treaty (PCT) Approach</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Month 12:</strong> File PCT applications for core business model patents</li>
                <li style={styles.bulletItem}><strong>Month 18:</strong> National phase entry decisions based on business expansion</li>
                <li style={styles.bulletItem}><strong>Month 30:</strong> Complete national phase filings in priority markets</li>
              </ul>
            </div>

            <h3 style={styles.h3}>Priority Markets for Patent Protection:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Canada:</strong> Similar legal framework and market opportunity</li>
              <li style={styles.bulletItem}><strong>United Kingdom:</strong> European market entry and fintech innovation hub</li>
              <li style={styles.bulletItem}><strong>Australia:</strong> English-speaking market with real estate innovation</li>
              <li style={styles.bulletItem}><strong>Germany:</strong> European Union market access and technology protection</li>
              <li style={styles.bulletItem}><strong>Japan:</strong> Asian market expansion and technology leadership</li>
            </ul>
          </div>

          <div id="regional-strategies" style={styles.section}>
            <h2 style={styles.h2}>Regional IP Strategies</h2>
            
            <div style={styles.warningBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>European Union Strategy</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>European Patent Office (EPO):</strong> File European patent applications for core technologies</li>
                <li style={styles.bulletItem}><strong>European Union Trademark (EUTM):</strong> File EUTM for comprehensive EU protection</li>
                <li style={styles.bulletItem}><strong>Brexit Considerations:</strong> Separate UK trademark filing post-Brexit</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Asia-Pacific Strategy</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Japan:</strong> Direct filing with Japan Patent Office, establish local counsel relationships</li>
                <li style={styles.bulletItem}><strong>Australia:</strong> Direct filing with IP Australia, consider innovation patents</li>
                <li style={styles.bulletItem}><strong>Market Entry:</strong> Align patent filings with business expansion timeline</li>
              </ul>
            </div>
          </div>
        </section>

        {/* IP Portfolio Valuation & Licensing */}
        <section id="valuation-licensing" style={styles.section}>
          <h1 style={styles.h1}>6. IP Portfolio Valuation & Licensing</h1>

          <div id="portfolio-valuation" style={styles.section}>
            <h2 style={styles.h2}>Patent Portfolio Valuation</h2>
            
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Valuation Method</th>
                  <th style={styles.tableHeaderCell}>Current Value</th>
                  <th style={styles.tableHeaderCell}>Year 5 Projection</th>
                  <th style={styles.tableHeaderCell}>Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Cost-Based</td>
                  <td style={styles.tableCell}>$1.2M</td>
                  <td style={styles.tableCell}>$2.5M</td>
                  <td style={styles.tableCell}>Development + prosecution costs</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>Market-Based</td>
                  <td style={styles.tableCell}>$3-5M</td>
                  <td style={styles.tableCell}>$8-12M</td>
                  <td style={styles.tableCell}>Comparable patent transactions</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Income-Based</td>
                  <td style={styles.tableCell}>$5-8M</td>
                  <td style={styles.tableCell}>$15-25M</td>
                  <td style={styles.tableCell}>Licensing + defensive value NPV</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div id="licensing-revenue" style={styles.section}>
            <h2 style={styles.h2}>Licensing Revenue Strategy</h2>
            
            <div style={styles.metricGrid}>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>$200K</div>
                <div style={styles.metricLabel}>Year 2 Licensing Revenue</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>$500K</div>
                <div style={styles.metricLabel}>Year 3 Licensing Revenue</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricNumber}>$2M+</div>
                <div style={styles.metricLabel}>Year 5 Licensing Revenue</div>
              </div>
            </div>

            <h3 style={styles.h3}>Target Licensees:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Real Estate Companies:</strong> Property management and investment firms</li>
              <li style={styles.bulletItem}><strong>Fintech Platforms:</strong> Financial technology and investment platforms</li>
              <li style={styles.bulletItem}><strong>Banking Institutions:</strong> Banks with real estate and investment services</li>
              <li style={styles.bulletItem}><strong>Technology Companies:</strong> Software and platform technology companies</li>
            </ul>

            <h3 style={styles.h3}>Licensing Models:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Platform Licensing:</strong> Complete marketplace platform licensing ($500K-1M annually)</li>
              <li style={styles.bulletItem}><strong>Component Licensing:</strong> Individual technology component licensing ($100K-300K annually)</li>
              <li style={styles.bulletItem}><strong>Patent Licensing:</strong> Defensive and revenue-generating patent licensing ($25K-75K annually)</li>
            </ul>
          </div>
        </section>

        {/* Implementation Timeline */}
        <section id="implementation" style={styles.section}>
          <h1 style={styles.h1}>7. Implementation Timeline</h1>

          <div id="phase-1" style={styles.section}>
            <h2 style={styles.h2}>Phase 1: Foundation and Core Patents (Months 1-6)</h2>
            
            <div style={styles.successBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Immediate Patent Filing - $400K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Month 1:</strong> File 12 core business model provisional patents</li>
                <li style={styles.bulletItem}><strong>Month 2-3:</strong> Complete non-provisional patent application drafting</li>
                <li style={styles.bulletItem}><strong>Month 4-6:</strong> File 12 core non-provisional patent applications</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Trade Secret and Legal Framework - $75K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Process Documentation:</strong> Document key operational methodologies</li>
                <li style={styles.bulletItem}><strong>Legal Template Development:</strong> Create trade secret protection templates</li>
                <li style={styles.bulletItem}><strong>Employee Training:</strong> Develop trade secret protection training programs</li>
              </ul>
            </div>
          </div>

          <div id="phase-2" style={styles.section}>
            <h2 style={styles.h2}>Phase 2: Technology and Expansion (Months 6-12)</h2>
            
            <div style={styles.warningBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Technology Patent Portfolio - $200K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Months 6-9:</strong> File 15 technology and process patent applications</li>
                <li style={styles.bulletItem}><strong>Months 9-12:</strong> File 12 defensive and strategic patent applications</li>
                <li style={styles.bulletItem}><strong>International Filing:</strong> Begin PCT filing process for core patents</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>International IP Strategy - $100K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>PCT Applications:</strong> File PCT applications for core business model patents</li>
                <li style={styles.bulletItem}><strong>Madrid Protocol:</strong> File Madrid Protocol trademark applications</li>
                <li style={styles.bulletItem}><strong>Domain Protection:</strong> Secure international domain names and digital assets</li>
              </ul>
            </div>
          </div>

          <div id="phase-3" style={styles.section}>
            <h2 style={styles.h2}>Phase 3: Defense and Optimization (Months 12-18)</h2>
            
            <div style={styles.riskBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Defensive Portfolio Development - $100K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Patent Landscape Analysis:</strong> Comprehensive competitor patent landscape analysis</li>
                <li style={styles.bulletItem}><strong>Freedom to Operate:</strong> Detailed FTO analysis for platform expansion</li>
                <li style={styles.bulletItem}><strong>Defensive Acquisition:</strong> Strategic patent acquisition for defensive purposes</li>
              </ul>
            </div>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Brand Protection and Monitoring - $25K</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Comprehensive Brand Protection:</strong> Complete trademark registration and protection</li>
                <li style={styles.bulletItem}><strong>Brand Monitoring:</strong> Implement comprehensive brand monitoring and enforcement</li>
                <li style={styles.bulletItem}><strong>IP Asset Management:</strong> Comprehensive IP asset management system</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ROI Projections */}
        <section id="roi-projections" style={styles.section}>
          <h1 style={styles.h1}>8. Return on Investment Projections</h1>

          <div id="value-creation" style={styles.section}>
            <h2 style={styles.h2}>IP Value Creation Timeline</h2>
            
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Phase</th>
                  <th style={styles.tableHeaderCell}>Portfolio Value</th>
                  <th style={styles.tableHeaderCell}>Licensing Revenue</th>
                  <th style={styles.tableHeaderCell}>Defensive Value</th>
                  <th style={styles.tableHeaderCell}>Total Value</th>
                </tr>
              </thead>
              <tbody>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Year 1 (Pilot Phase)</td>
                  <td style={styles.tableCell}>$2-3M</td>
                  <td style={styles.tableCell}>$0</td>
                  <td style={styles.tableCell}>$500K</td>
                  <td style={styles.tableCell}>$2.5-3.5M</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>Year 2-3 (Expansion)</td>
                  <td style={styles.tableCell}>$5-8M</td>
                  <td style={styles.tableCell}>$200K-500K</td>
                  <td style={styles.tableCell}>$500K-1M</td>
                  <td style={styles.tableCell}>$5.7-9.5M</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Year 4-5 (Maturity)</td>
                  <td style={styles.tableCell}>$15-25M</td>
                  <td style={styles.tableCell}>$1-2M</td>
                  <td style={styles.tableCell}>$1M+</td>
                  <td style={styles.tableCell}>$17-28M</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div id="cost-benefit" style={styles.section}>
            <h2 style={styles.h2}>Cost-Benefit Analysis</h2>
            
            <div style={styles.highlightBox}>
              <div style={styles.metricGrid}>
                <div style={styles.metricCard}>
                  <div style={{...styles.metricNumber, color: 'white'}}>$940K</div>
                  <div style={{...styles.metricLabel, color: 'rgba(255,255,255,0.9)'}}>Total IP Investment (18 months)</div>
                </div>
                <div style={styles.metricCard}>
                  <div style={{...styles.metricNumber, color: 'white'}}>$15-25M</div>
                  <div style={{...styles.metricLabel, color: 'rgba(255,255,255,0.9)'}}>Portfolio Value at Maturity</div>
                </div>
                <div style={styles.metricCard}>
                  <div style={{...styles.metricNumber, color: 'white'}}>16x-27x</div>
                  <div style={{...styles.metricLabel, color: 'rgba(255,255,255,0.9)'}}>ROI Multiple</div>
                </div>
                <div style={styles.metricCard}>
                  <div style={{...styles.metricNumber, color: 'white'}}>$2M+</div>
                  <div style={{...styles.metricLabel, color: 'rgba(255,255,255,0.9)'}}>Annual Licensing Revenue at Maturity</div>
                </div>
              </div>
            </div>

            <h3 style={styles.h3}>Strategic Value Creation:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Immediate Protection:</strong> 39 patent applications protecting core innovations</li>
              <li style={styles.bulletItem}><strong>Competitive Moat:</strong> Multiple overlapping patents prevent competitor copying</li>
              <li style={styles.bulletItem}><strong>Revenue Generation:</strong> Licensing opportunities to real estate and fintech companies</li>
              <li style={styles.bulletItem}><strong>Exit Value Enhancement:</strong> Strong IP portfolio increases acquisition value</li>
              <li style={styles.bulletItem}><strong>Partnership Enablement:</strong> IP-enabled strategic partnerships and integrations</li>
            </ul>

            <div style={styles.successBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Investment in IP is Investment in Market Leadership</h4>
              <p style={styles.paragraph}>
                GusHome's comprehensive intellectual property strategy transforms innovative technology into protected, valuable assets that drive revenue, enable partnerships, and create sustainable competitive advantages in the revolutionary real estate securities marketplace.
              </p>
              <p style={{...styles.paragraph, marginBottom: '0'}}>
                The comprehensive IP portfolio positions GusHome as the definitive leader in real estate marketplace innovation, protected by an extensive patent portfolio, trade secrets, and brand assets that create multiple revenue streams while defending against competitive threats.
              </p>
            </div>
          </div>
        </section>

        {/* Add a note about the continuation */}
        <div style={{...styles.noteBox, marginTop: '40px'}}>
          <h3 style={{...styles.h3, marginTop: '0'}}>Complete IP Strategy Document</h3>
          <p style={{...styles.paragraph, marginBottom: '0'}}>
            This intellectual property strategy overview covers the key components of our comprehensive 46-page IP strategy. The complete document includes detailed patent claims, international filing strategies, competitive landscape analysis, enforcement procedures, risk management protocols, and long-term IP evolution strategies.
          </p>
          <p style={{...styles.paragraph, marginBottom: '0'}}>
            For the complete document with all detailed patent descriptions, trade secret documentation, international strategies, and comprehensive enforcement plans, please download the full PDF version using the link in the sidebar.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IP;